import { useState, useEffect, useCallback } from "react";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Toaster, toast } from 'sonner'
import { getUserUserInviteDetails, submitInvitationPassword } from '../../apis/Settings' 

const PasswordPage = () => {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { code } = useParams();

  const navigate = useNavigate();

  useEffect(() => {
    getUserUserInviteDetails(code).then(resp => {
      if(resp?.status === 200){
        setUserEmail(resp?.data?.data?.username)
      }else{
        toast.error("Unable to fetch invite details. Invalid invitation link.");
      }
    });
  }, [code]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const performNavigation = useCallback(() => {  
    const timeoutId = setTimeout(() => {
      // if (window.location.pathname === '/auth/login') {
      //   window.location.replace('/auth/login');
      // }
    navigate('/auth/login', { replace: true });
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    submitInvitationPassword(code, formData).then(resp => {
      if(resp.status === 200 || resp.status === 201){
        toast.success("Password set successfully. Please login", { theme: "colored" })
        performNavigation();
      }else{
        toast.error("Unable to set password. please try again.", { theme: "colored" })
        setIsLoading(false);
      }
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md animate-fade-in">
        <img src={process.env.PUBLIC_URL + "/images/logo.png"} alt="lipachat logo" width="120px" className="mx-auto mb-2"/>
        
        <div className="w-full max-w-md bg-white/95 border border-gray-200 backdrop-blur-sm shadow-lg h-max mx-auto my-auto rounded-lg">
          {/* Card Header */}
          <div className="text-center space-y-4 p-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">
                Set Your Password
              </h2>
              <p className="text-gray-600">
                Create a secure password for your account
              </p>
            </div>
          </div>
          
          {/* Card Content */}
          <div className="space-y-6 p-6 pt-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-0">
                <label htmlFor="emailAddress" className="text-sm font-medium block mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="emailAddress"
                    disabled={true}
                    type="email"
                    placeholder="Enter your email"
                    value={userEmail}
                    className={`cursor-not-allowed bg-gray-100 w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 transition-all duration-300`}
                  />
                </div>
                {errors.emailAddress && (
                  <p className="text-sm text-red-500 animate-fade-in mt-1">{errors.emailAddress}</p>
                )}
              </div>

              <div className="space-y-0">
                <label htmlFor="password" className="text-sm font-medium block mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Create a password"
                    value={formData?.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 transition-all duration-300 ${
                      errors.password 
                        ? "border-red-500 focus:ring-red-500" 
                        : "border-gray-300 focus:ring-blue-500"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-500 animate-fade-in mt-1">{errors.password}</p>
                )}
              </div>

              <div className="space-y-0">
                <label htmlFor="confirmPassword" className="text-sm font-medium block mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 transition-all duration-300 ${
                      errors.confirmPassword 
                        ? "border-red-500 focus:ring-red-500" 
                        : "border-gray-300 focus:ring-blue-500"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-red-500 animate-fade-in mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full mt-8 py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-md hover:from-blue-500 hover:to-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Setting Password...
                  </div>
                ) : (
                  "Set Password"
                )}
              </button>
            </form>

            <div className="text-center text-sm text-gray-600">
              Already have an account?{" "}
              <Link to="/auth/login" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
       <Toaster position="top-right" expand={false} richColors/>
    </div>
  );
};

export default PasswordPage;